import React from 'react';
import { QAlert, QButton, QDivider, QHeading, QStack, useCurrentUser } from '@qualio/ui-components';
import { useTranslation } from 'react-i18next';
import DesignControlsPermissions from '../FormComponents/DesignControlsPermissions';
import QualityEventsPermissions from '../FormComponents/QualityEventsPermissions';
import SuppliersPermissions from '../FormComponents/SuppliersPermissions';
import CompliancePermissions from '../FormComponents/CompliancePermissions';
import { PermissionWrapper } from '../../views/EditUser/EditUserForm/components/PermissionWrapper';
import { LegacyPublicApiUserPermission } from './LegacyPublicApiUserPermission';

type PermissionsFormElementsProps = {
  resetPermissions: () => void;
  isHumanUser: boolean;
};

export const PermissionsFormElements: React.FC<PermissionsFormElementsProps> = ({ resetPermissions, isHumanUser }) => {
  const user = useCurrentUser();
  const { capabilities } = user;
  const { t } = useTranslation();

  const anyRelevantCapabilityEnabled =
    capabilities === null
      ? false
      : Object.keys(capabilities)
          .filter((capability) => !['sso', 'premium_integrations'].includes(capability))
          .some((capability) => capabilities[capability as keyof typeof capabilities]);

  return (
    <div>
      {anyRelevantCapabilityEnabled && (
        <>
          <QStack spacing={4}>
            <QHeading fontWeight="bold" size="md" mt={2}>
              {t('editUser.permissions')}
            </QHeading>
            <PermissionWrapper
              shouldRender={capabilities?.events ?? false}
              header={t('editUser.events')}
              description={t('editUser.eventsDescription')}
            >
              <>
                <QAlert description={t('editUser.eventsWarning')} status="info" />
                <QualityEventsPermissions />
              </>
            </PermissionWrapper>
            <PermissionWrapper
              shouldRender={capabilities?.suppliers ?? false}
              header={t('editUser.suppliers')}
              description={t('editUser.suppliersDescription')}
            >
              <SuppliersPermissions />
            </PermissionWrapper>
            <PermissionWrapper
              shouldRender={capabilities?.design_controls ?? false}
              header={t('editUser.designControls')}
              description={t('editUser.designControlsDescription')}
            >
              <DesignControlsPermissions />
            </PermissionWrapper>
            <PermissionWrapper
              shouldRender={Boolean(capabilities?.compliance)}
              header={t('editUser.compliance')}
              description={t('editUser.complianceDescription')}
            >
              <CompliancePermissions />
            </PermissionWrapper>
            {capabilities?.api && isHumanUser && <LegacyPublicApiUserPermission />}
            <QButton data-cy="reset-button" variant="outline" size="sm" onClick={resetPermissions}>
              {t('editUser.resetPermissions')}
            </QButton>
          </QStack>
          <QDivider />
        </>
      )}
    </div>
  );
};
